const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const STORAGE_KEY = 'halr_signup_utm';

export type SignupUtm = Partial<Record<(typeof UTM_KEYS)[number], string>> & {
  landing_path?: string;
  referrer?: string;
};

/** First non-empty value wins. Later pages must not replace the signup touch. */
export function mergeSignupTouch(
  stored: SignupUtm | null | undefined,
  input: { search?: string; pathname?: string; referrer?: string; pageOrigin?: string },
): SignupUtm {
  const params = new URLSearchParams(input.search || '');
  const utm: SignupUtm = { ...(stored || {}) };
  for (const key of UTM_KEYS) {
    if (utm[key]?.trim()) continue;
    const value = params.get(key)?.trim();
    if (value) utm[key] = value;
  }
  if (!utm.landing_path?.trim() && input.pathname?.trim()) utm.landing_path = input.pathname.trim();
  const referrer = externalReferrer(input.referrer, input.pageOrigin);
  if (!utm.referrer?.trim() && referrer) utm.referrer = referrer;
  return utm;
}

function externalReferrer(referrer?: string, pageOrigin?: string): string {
  const value = referrer?.trim();
  if (!value) return '';
  try {
    const url = new URL(value);
    if (pageOrigin && url.origin === pageOrigin) return '';
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
}

export function captureSignupUtmFromLocation(
  search = typeof window !== 'undefined' ? window.location.search : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
  referrer = typeof document !== 'undefined' ? document.referrer : '',
): SignupUtm {
  const utm = mergeSignupTouch(readStoredSignupUtm(), {
    search,
    pathname,
    referrer,
    pageOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
  });
  const hasTouch = UTM_KEYS.some((key) => Boolean(utm[key])) || Boolean(utm.landing_path) || Boolean(utm.referrer);
  if (hasTouch && typeof window !== 'undefined') {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  }
  return utm;
}

export function readStoredSignupUtm(): SignupUtm | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SignupUtm;
  } catch {
    return null;
  }
}

export async function pingLifecycle(apiFetch: (url: string, init?: any) => Promise<Response>, event = 'first_login') {
  const utm = readStoredSignupUtm() || captureSignupUtmFromLocation();
  await apiFetch('/api/lifecycle/ping', {
    method: 'POST',
    body: { event, utm },
  }).catch(() => null);
}
